import { useState, useEffect } from 'react'
import { Search, Filter, X, ChevronDown } from 'lucide-react'
import { ProductGrid } from '@/components/products/product-grid'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { useCartStore } from '@/lib/store'
import { Toast } from '@/components/ui/toast'

// Define available categories
const CATEGORIES = [
  'Chaussure',
  'Fashion',
  'Home & Garden',
  'Soins et Santer',
  'Sport',
  'Books',
  'Toys',
  'Other'
]

// Define sort options
const SORT_OPTIONS = [
  { value: 'created_at.desc', label: 'products.sort.newest' },
  { value: 'price.asc', label: 'products.sort.priceAsc' },
  { value: 'price.desc', label: 'products.sort.priceDesc' }
]

export function ProductsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000])
  const [sortBy, setSortBy] = useState('created_at.desc')
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()
  const addToCart = useCartStore((state) => state.addItem)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  // Fetch products with filters
  useEffect(() => {
    fetchProducts()
  }, [searchQuery, selectedCategories, priceRange, sortBy])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      
      // Start building the query
      let query = supabase
        .from('products')
        .select('*')
        .eq('status', 'active')
      
      // Apply search filter if provided
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
      }
      
      // Apply category filter if any categories are selected
      if (selectedCategories.length > 0) {
        query = query.in('category', selectedCategories)
      }
      
      // Apply price range filter
      query = query
        .gte('price', priceRange[0])
        .lte('price', priceRange[1])
        .eq('is_deleted', false)
      
      // Apply sorting
      const [sortField, sortOrder] = sortBy.split('.')
      query = query.order(sortField, { ascending: sortOrder === 'asc' })
      
      // Execute the query
      const { data, error } = await query
      
      if (error) {
        console.error('Error fetching products:', error)
        return
      }
      
      setProducts(data || [])
    } catch (err) {
      console.error('Error in fetchProducts:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    )
  }

  const handlePriceChange = (index: number, value: number) => {
    setPriceRange(prev => {
      const newRange = [...prev] as [number, number]
      newRange[index] = value
      return newRange
    })
  }

  const clearFilters = () => {
    setSelectedCategories([])
    setPriceRange([0, 1000])
    setSortBy('created_at.desc')
  }

  const handleAddToCart = (product: any) => {
    addToCart(product)
    setToastMessage(t('products.addedToCart'))
    setShowToast(true)
  }

  return (
    <div className="bg-white">
      {showToast && (
        <Toast
          type="success"
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      <div className="max-w-7xl mx-auto py-8 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="pb-6 sm:pb-10">
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-gray-900">{t('products.title')}</h1>
          <p className="mt-2 sm:mt-4 text-sm sm:text-base text-gray-500">
            {t('products.subtitle')}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-lg w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>

          <div className="flex items-center w-full sm:w-auto space-x-2">
            <div className="relative inline-block text-left w-full sm:w-auto">
              <button
                type="button"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-5 w-5 mr-2 text-gray-400" />
                {t('products.filters')}
                <ChevronDown className="ml-2 h-4 w-4" />
              </button>
            </div>
            
            <div className="relative inline-block text-left w-full sm:w-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="block w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <option value="" disabled>{t('products.sort.title')}</option>
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white shadow-md rounded-lg p-4 mb-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('products.filters')}</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-primary hover:text-primary-dark"
              >
                {t('common.reset')}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Categories */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">{t('products.category')}</h4>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIES.map((category) => (
                    <div key={category} className="flex items-center">
                      <input
                        id={`category-${category}`}
                        type="checkbox"
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        checked={selectedCategories.includes(category)}
                        onChange={() => handleCategoryToggle(category)}
                      />
                      <label
                        htmlFor={`category-${category}`}
                        className="ml-2 text-sm text-gray-700"
                      >
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Price Range */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">{t('products.price')}</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">${priceRange[0]}</span>
                    <span className="text-sm text-gray-600">${priceRange[1]}</span>
                  </div>
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <input
                        type="range"
                        min="0"
                        max="1000"
                        step="10"
                        value={priceRange[0]}
                        onChange={(e) => handlePriceChange(0, parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">Min</span>
                    </div>
                    <div className="flex-1">
                      <input
                        type="range"
                        min="0"
                        max="1000"
                        step="10"
                        value={priceRange[1]}
                        onChange={(e) => handlePriceChange(1, parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">Max</span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      min="0"
                      max={priceRange[1]}
                      value={priceRange[0]}
                      onChange={(e) => handlePriceChange(0, parseInt(e.target.value))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                    <span className="text-gray-500">-</span>
                    <input
                      type="number"
                      min={priceRange[0]}
                      max="1000"
                      value={priceRange[1]}
                      onChange={(e) => handlePriceChange(1, parseInt(e.target.value))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowFilters(false)}
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        )}

        {/* Active Filters */}
        {(selectedCategories.length > 0 || priceRange[0] > 0 || priceRange[1] < 1000) && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm text-gray-700">{t('products.activeFilters')}:</span>
            
            {selectedCategories.map(category => (
              <span 
                key={category}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {category}
                <button
                  type="button"
                  className="flex-shrink-0 ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none focus:bg-blue-500 focus:text-white"
                  onClick={() => handleCategoryToggle(category)}
                >
                  <span className="sr-only">Remove filter for {category}</span>
                  <X className="h-2 w-2" />
                </button>
              </span>
            ))}
            
            {(priceRange[0] > 0 || priceRange[1] < 1000) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ${priceRange[0]} - ${priceRange[1]}
                <button
                  type="button"
                  className="flex-shrink-0 ml-1.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none focus:bg-green-500 focus:text-white"
                  onClick={() => setPriceRange([0, 1000])}
                >
                  <span className="sr-only">Remove price filter</span>
                  <X className="h-2 w-2" />
                </button>
              </span>
            )}
            
            <button
              type="button"
              className="text-sm text-primary hover:text-primary-dark"
              onClick={clearFilters}
            >
              {t('common.clearAll')}
            </button>
          </div>
        )}

        <div className="mt-6 sm:mt-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Search className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('common.noResults')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('products.noProductsFound')}
              </p>
              <Button
                variant="primary"
                className="mt-4"
                onClick={clearFilters}
              >
                {t('common.clearFilters')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-y-8 gap-x-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-6">
              {products.map((product) => (
                <div key={product.id} className="group relative h-full flex flex-col">
                  <a href={`/products/${product.id}`} className="block flex-grow">
                    <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-200">
                      <img
                        src={product.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                        alt={product.title}
                        className="h-full w-full object-cover object-center group-hover:opacity-75 transition-opacity"
                      />
                    </div>
                    <div className="mt-4 flex justify-between">
                      <div className="max-w-[70%]">
                        <h3 className="text-sm text-gray-700 line-clamp-1">
                          {product.title}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 line-clamp-1">{product.category}</p>
                      </div>
                      <p className="text-sm font-medium text-gray-900">${product.price}</p>
                    </div>
                  </a>
                  <div className="mt-4">
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full text-xs sm:text-sm flex items-center justify-center"
                      onClick={() => handleAddToCart(product)}
                    >
                      <span>{t('products.addToCart')}</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}